/**
 * Type stubs for the IAB CMP APIs we look for on `window`.
 * These are intentionally minimal — only the methods the SDK calls.
 */

/* -------------------------------------------------------------------------- */
/* TCF v2 — IAB Europe Transparency & Consent Framework                       */
/* -------------------------------------------------------------------------- */

export interface TcfData {
  /** TCF event status — 'tcloaded', 'cmpuishown', 'useractioncomplete'. */
  eventStatus?: string;
  /** Whether the user is in a GDPR jurisdiction per the CMP. */
  gdprApplies?: boolean;
  /** Decoded purpose consents 1..n. */
  purpose?: { consents?: Record<string, boolean> };
  /** Decoded vendor consents 1..n. */
  vendor?: { consents?: Record<string, boolean> };
  tcString?: string;
  cmpStatus?: string;
  listenerId?: number;
}

export type TcfCallback = (data: TcfData | null, success: boolean) => void;

export interface TcfApi {
  (command: 'addEventListener' | 'getTCData' | 'removeEventListener', version: number, cb: TcfCallback, listenerId?: number): void;
}

/* -------------------------------------------------------------------------- */
/* USP v1 — IAB CCPA US Privacy String                                        */
/* -------------------------------------------------------------------------- */

export interface UspData {
  /** Format: "1YN-" / "1YY-" / "1---" — version, notice, opt-out, LSPA. */
  uspString: string;
}

export type UspCallback = (data: UspData | null, success: boolean) => void;

export interface UspApi {
  (command: 'getUSPData', version: 1, cb: UspCallback): void;
}

/* -------------------------------------------------------------------------- */
/* GPP — IAB Global Privacy Platform                                           */
/* -------------------------------------------------------------------------- */

export interface GppData {
  gppString?: string;
  applicableSections?: number[];
}

export type GppCallback = (data: GppData | null, success: boolean) => void;

export interface GppApi {
  (command: string, cb: GppCallback, parameter?: unknown): void;
}

/* -------------------------------------------------------------------------- */
/* Window augmentation                                                        */
/* -------------------------------------------------------------------------- */

declare global {
  interface Window {
    __tcfapi?: TcfApi;
    __uspapi?: UspApi;
    __gpp?: GppApi;
  }
}

export function readTcfApi(): TcfApi | undefined {
  return typeof window !== 'undefined' ? window.__tcfapi : undefined;
}

export function readUspApi(): UspApi | undefined {
  return typeof window !== 'undefined' ? window.__uspapi : undefined;
}

export function readGppApi(): GppApi | undefined {
  return typeof window !== 'undefined' ? window.__gpp : undefined;
}
